import { useDispatch, useSelector } from "react-redux"
import React, { useEffect, useState, useRef } from "react"
import {
  TASpamBrand,
  TAbalanceLoaded,
  TAbrandECommerce,
  TAbrandHeardFrom,
  TAbrandSalesReadiness,
  TAbrandSize,
  TAcampaignCompleted,
  TAcampaignManagementContinues,
  TAdemoCampaignCreated,
  TAdirectedToAgencyBasedWork,
  TAfindAllBrands,
  TAinformationMailSent,
  TAmeetingPlanned,
  TApersonalAccount,
  TAupdateNote,
  TAdeleteNote,
  TAupdateBrandMeeting,
  TAdeleteBrandMeeting,
  TAupdateBrandStage,
  TAsetResponsibleAdmin,
  TAgetCampaignNotes,
  TAbrandSetCpmAim,
  TAbrandSetLeadStyle,
  TAbrandSetUtm,
  TAbrandReferenceChange,
  TAsetBrandClassification,
} from "../../../services/brandAPI"
import { selectUser } from "../../../redux/store/auth/authSlice"
import { DataTableSortStatus } from "mantine-datatable"
import { setPageTitle } from "../../../redux/store/themeConfigSlice"
import { Brand, Meeting, Note, Stage, Admin } from "../../../types/brandDatas"

import { TAgetQuizzes } from "../../../services/quizAPI"
import { useNavigate } from "react-router-dom"
import CustomerSuccessFilters, { AIFilters, defaultAIFilters } from "./CustomerSuccessFilters"
import NotesModal from "../allBrands/components/NotesModal"
import MeetingModal from "../allBrands/components/MeetingModal"
import BrandOperationsModal from "../allBrands/components/BrandOperationsModal"
import StagesModal from "../allBrands/components/StagesModal"
import EcommerceModal from "../allBrands/components/EcommerceModal"
import BrandSizeModal from "../allBrands/components/BrandSizeModal"
import BrandStateHistoryModal from "../allBrands/components/BrandStateHistoryModal"
import ResponsibleAdminModal from "../allBrands/components/ResponsibleAdminModal"
import CpmAimModal from "../allBrands/components/CpmAimModal"
import LeadStyleModal from "../allBrands/components/LeadStyleModal"
import UtmModal from "../allBrands/components/UtmModal"
import ReferenceChangeModal from "../allBrands/components/ReferenceChangeModal"
import {
  BrandListApiResponse,
  BrandListFilters,
  BrandListRecord,
  BrandOperationKey,
  BrandOperationsState,
  BrandStateHistoryItem,
  CampaignNote,
  MeetingPayload,
  Quiz,
} from "../allBrands/types"
import { getErrorStatus, getErrorMessage, isAbortError } from "../../../types/error"
import CategoryModal from "../allBrands/components/CategoryModal"
import ClassificationModal, { Classification } from "../allBrands/components/ClassificationModal"
import BrandStateLabel from "../allBrands/components/BrandStateLabel"
import StickyDataTable from "../../../components/StickyDataTable"
import GroupedColumns from "../allBrands/components/GroupedColumns"
import { columnConfig } from "../allBrands/components/columns"
import AIHealthAnalysisModal from "./AIHealthAnalysisModal"

const defaultBrandOperationsState: BrandOperationsState = {
  meeting_planned: false,
  information_mail_sent: false,
  directed_to_agency_based_work: false,
  demo_campaign_created: false,
  balance_loaded: false,
  campaign_management_continues: false,
  campaign_completed: false,
  personal_account: false,
}

// Avatar renk paletleri
const avatarColors = [
  { bg: "bg-indigo-100 dark:bg-indigo-900/50", text: "text-indigo-600 dark:text-indigo-300" },
  { bg: "bg-blue-100 dark:bg-blue-900/50", text: "text-blue-600 dark:text-blue-300" },
  { bg: "bg-pink-100 dark:bg-pink-900/50", text: "text-pink-600 dark:text-pink-300" },
  { bg: "bg-teal-100 dark:bg-teal-900/50", text: "text-teal-600 dark:text-teal-300" },
  { bg: "bg-orange-100 dark:bg-orange-900/50", text: "text-orange-600 dark:text-orange-300" },
  { bg: "bg-purple-100 dark:bg-purple-900/50", text: "text-purple-600 dark:text-purple-300" },
]

const getInitials = (name: string): string => {
  if (!name) {
    return "??"
  }
  const words = name.split(" ")
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
}

const getAvatarColor = (name: string) => {
  if (!name) {
    return avatarColors[0]
  }
  const index = name.charCodeAt(0) % avatarColors.length
  return avatarColors[index]
}

const getHealthScoreColor = (score: number): string => {
  if (score >= 75) {
    return "bg-green-500"
  }
  if (score >= 50) {
    return "bg-yellow-500"
  }
  return "bg-red-500"
}

const formatDate = (dateValue?: string | Date): string => {
  if (!dateValue) {
    return "-"
  }
  const date = new Date(dateValue)
  return date.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })
}

const formatCurrency = (amount?: number): string => {
  if (amount === undefined || amount === null) {
    return "₺0"
  }
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: 0,
  }).format(amount)
}

// Helper function to get admin email from responsible_admin
const getAdminEmail = (admin: string | Admin): string => {
  if (typeof admin === "string") {
    return admin
  }
  return admin.email || ""
}

// Helper function to get responsible admins as string array
const getResponsibleAdminsArray = (
  responsibleAdmin?: string | string[] | Admin[]
): string[] | undefined => {
  if (!responsibleAdmin) {
    return undefined
  }
  if (typeof responsibleAdmin === "string") {
    return [responsibleAdmin]
  }
  if (Array.isArray(responsibleAdmin)) {
    return responsibleAdmin.map((admin) => getAdminEmail(admin))
  }
  return undefined
}

// Status Chip Component - Exact style from the provided UI image
const StatusChip: React.FC<{ brandState: string }> = ({ brandState }) => {
  const getChipStyle = (state: string) => {
    // Active states - Green
    if (
      state.includes("has_balance_loaded") ||
      state.includes("loaded_balance_multiple_times") ||
      state.includes("collaboration_completed")
    ) {
      return {
        bg: "bg-[#ECFDF5]",
        text: "text-[#059669]",
        label: "Active",
      }
    }
    // At Risk states - Red
    if (
      state.includes("balance_not_loaded") ||
      state.includes("has_no_applications")
    ) {
      return {
        bg: "bg-[#FEF2F2]",
        text: "text-[#DC2626]",
        label: "Churned",
      }
    }
    // Pending/Ready - Yellow
    return {
      bg: "bg-[#FFFBEB]",
      text: "text-[#D97706]",
      label: "Pending",
    }
  }

  const style = getChipStyle(brandState)

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-bold ${style.bg} ${style.text}`}>
      {style.label}
    </span>
  )
}

/**
 * AI Health Score Calculation System
 * 
 * This function calculates a customer health score (0-100) based on multiple factors.
 * The score helps identify customers who need attention.
 * 
 * SCORING ALGORITHM:
 * - Balance Activity (30 points max): Based on balance loading behavior
 * - Campaign Engagement (25 points max): Active campaigns and completion rate
 * - Application Performance (20 points max): Application success rate
 * - Login Recency (15 points max): How recently the user logged in
 * - Communication (10 points max): Notes and meetings frequency
 * 
 * AI PROMPT SUGGESTION for GPT integration:
 * "You are a Customer Success AI assistant. Given the following brand metrics:
 * - Total Balance: {totalBalance}
 * - Active Campaigns: {activeCampaigns}
 * - Total Campaigns: {totalCampaigns}
 * - Last Login: {lastLogin}
 * - Application Success Rate: {applicationRate}
 * - Notes Count: {notesCount}
 * - Brand State: {brandState}
 * 
 * Calculate a health score (0-100) where:
 * - 80-100: Healthy - High engagement, good metrics
 * - 60-79: Moderate - Some activity but needs attention
 * - 40-59: At Risk - Low engagement, potential churn
 * - 0-39: Critical - Immediate action needed
 * 
 * Provide the score and a one-sentence explanation."
 */
const calculateHealthScore = (record: BrandListRecord): number => {
  let score = 0

  // Balance Activity (30 points)
  if (record.totalBalance && record.totalBalance > 0) {
    score += 15
    if (record.totalBalance > 5000) {
      score += 10
    }
    if (record.totalSpentBalance && record.totalSpentBalance > 0) {
      score += 5
    }
  }

  // Campaign Engagement (25 points)
  if (record.activeCampaigns && record.activeCampaigns > 0) {
    score += 15
    if (record.activeCampaigns > 2) {
      score += 5
    }
  }
  if (record.totalCampaigns && record.totalCampaigns > 0) {
    score += 5
  }

  // Application Performance (20 points)
  if (record.activeApplications && record.activeApplications > 0) {
    score += 10
    if (record.activeApplicationsToTotalApplicationsRatio && 
        record.activeApplicationsToTotalApplicationsRatio > 0.5) {
      score += 10
    }
  }

  // Login Recency (15 points)
  if (record.last_login) {
    const daysSinceLogin = Math.floor(
      (Date.now() - new Date(record.last_login).getTime()) / (1000 * 60 * 60 * 24)
    )
    if (daysSinceLogin <= 7) {
      score += 15
    } else if (daysSinceLogin <= 30) {
      score += 10
    } else if (daysSinceLogin <= 90) {
      score += 5
    }
  }

  // Communication (10 points)
  if (record.notes && record.notes.length > 0) {
    score += 5
  }
  if (record.meetings && record.meetings.length > 0) {
    score += 5
  }

  // Clamp to 0-100
  return Math.min(100, Math.max(0, score))
}

/**
 * Health Score 2: Kampanya Süreç Verimliliği & İş Birliği Tamamlama Skoru
 * 
 * Bu skor 100 puandan başlar ve problem alanları için puan DÜŞÜRÜR.
 * 
 * ÖNCELİK SIRASI (En önemliden az önemliye):
 * 
 * 1. BÜTÇE ERİTME VERİMLİLİĞİ (-30 puan max) ⭐⭐⭐
 *    Para yüklendi ama harcanmıyor = En büyük problem
 *    - %0 harcama: -30 (Para hiç kullanılmıyor)
 *    - <%20 harcama: -20
 *    - <%40 harcama: -10
 * 
 * 2. BLOKE PARA / İŞ BİRLİĞİ TAMAMLANMAMA (-25 puan max) ⭐⭐⭐
 *    Yüksek bloke = İş birlikleri tamamlanamıyor, süreç sıkışmış
 *    - %60+ bloke: -25 (Çok fazla yarım kalmış iş birliği)
 *    - %40-60 bloke: -15
 *    - %20-40 bloke: -8
 * 
 * 3. KAMPANYA SÜRECİ İLERLEME HIZI (-20 puan max) ⭐⭐
 *    Pasif/Taslak kampanya oranı yüksekse süreç ilerlemiyor
 *    - Pasif oran >%70: -15 (Kampanyalar duruyor)
 *    - Taslak > Aktif: -10 (Başlatılamıyor)
 *    - Aktif kampanya yok: -20 (Hiç ilerleme yok)
 * 
 * 4. İŞ BİRLİĞİ TAMAMLAMA ORANI (-15 puan max) ⭐⭐
 *    Düşük aktif/toplam başvuru = Başvurular ilerleyemiyor
 *    - <%3 aktif: -15 (Neredeyse hiç tamamlanmıyor)
 *    - <%10 aktif: -10
 *    - <%20 aktif: -5
 * 
 * 5. MALİYET VERİMLİLİĞİ (-10 puan max) ⭐
 *    Başvuru başına maliyet çok yüksekse verimli değil
 *    - CPA > 8000₺: -10
 *    - CPA > 4000₺: -5
 * 
 * BONUS PUANLAR:
 * - Yüksek harcama oranı (%60+): +5
 * - Düşük bloke oranı (<%10): +5
 * - Yüksek aktif kampanya oranı (%50+): +5
 * 
 * Renk Kodları:
 * - 80-100: 🟣 Excellent - Süreç çok verimli
 * - 60-79: 🔵 Good - İyi gidiyor
 * - 40-59: 🟡 Attention - Süreç yavaş, müdahale gerekli
 * - 0-39: 🔴 Critical - Süreç durmuş, acil aksiyon
 */
const calculateHealthScore2 = (record: BrandListRecord): number => {
  let score = 100 // Başlangıç puanı

  // ============================================
  // 1. BÜTÇE ERİTME VERİMLİLİĞİ (-30 puan max) ⭐⭐⭐
  // En önemli metrik: Para yüklendi mi, harcanıyor mu?
  // ============================================
  if (record.totalBalance && record.totalBalance > 0) {
    const spentRatio = record.totalSpentToTotalBalanceRatio || 0
    
    if (spentRatio === 0) {
      score -= 30 // KRİTİK: Para yüklendi ama HİÇ harcanmadı
    } else if (spentRatio < 0.1) {
      score -= 25 // ÇOK YÜKSEK: %10'dan az harcama
    } else if (spentRatio < 0.2) {
      score -= 20 // YÜKSEK: %20'den az harcama
    } else if (spentRatio < 0.4) {
      score -= 10 // DİKKAT: %40'tan az harcama
    } else if (spentRatio < 0.5) {
      score -= 5 // UYARI: %50'den az harcama
    }
    // %50+ harcama = İyi, ceza yok
  } else if (record.balance && record.balance > 0 && (!record.totalSpentBalance || record.totalSpentBalance === 0)) {
    // Bakiye var ama hiç harcama yok
    score -= 25
  }

  // ============================================
  // 2. BLOKE PARA / İŞ BİRLİĞİ TAMAMLANMAMA (-25 puan max) ⭐⭐⭐
  // Yüksek bloke = Süreçte sıkışmış başvurular, iş birlikleri tamamlanamıyor
  // ============================================
  if (record.totalBalance && record.totalBalance > 0) {
    const blockedRatio = record.totalBlockedToTotalBalanceRatio || 0
    const totalBlocks = record.totalBlocks || 0
    
    if (blockedRatio >= 0.6) {
      score -= 25 // KRİTİK: %60+ bloke - Çok fazla yarım kalmış
    } else if (blockedRatio >= 0.4) {
      score -= 15 // YÜKSEK: %40-60 bloke
    } else if (blockedRatio >= 0.2) {
      score -= 8 // DİKKAT: %20-40 bloke
    } else if (blockedRatio >= 0.1) {
      score -= 3 // UYARI: %10-20 bloke
    }
    
    // Mutlak bloke miktarı da önemli (büyük rakamlar)
    if (totalBlocks > 50000) {
      score -= 5 // 50K+ bloke ek ceza
    }
  }

  // ============================================
  // 3. KAMPANYA SÜRECİ İLERLEME HIZI (-20 puan max) ⭐⭐
  // Aktif/Pasif/Taslak oranları sürecin hızını gösterir
  // ============================================
  if (record.totalCampaigns && record.totalCampaigns > 0) {
    const activeCount = record.activeCampaigns || 0
    const inactiveRatio = record.inactiveCampaignsToTotalCampaignsRatio || 0
    const draftCount = record.draftCampaigns || 0
    const activeRatio = record.activeCampaignsToTotalCampaignsRatio || 0
    
    // Hiç aktif kampanya yoksa
    if (activeCount === 0) {
      score -= 20 // KRİTİK: Hiç aktif kampanya yok, süreç durmuş
    } else {
      // Pasif kampanya oranı yüksekse
      if (inactiveRatio > 0.8) {
        score -= 15 // ÇOK YÜKSEK: %80+ pasif
      } else if (inactiveRatio > 0.6) {
        score -= 10 // YÜKSEK: %60+ pasif
      } else if (inactiveRatio > 0.4) {
        score -= 5 // DİKKAT: %40+ pasif
      }
      
      // Taslak sayısı aktiften fazlaysa
      if (draftCount > activeCount && draftCount >= 3) {
        score -= 8 // Taslaklar başlatılamıyor
      } else if (draftCount > activeCount) {
        score -= 4
      }
    }
  } else {
    // Hiç kampanya yoksa (henüz başlamamış)
    score -= 15
  }

  // ============================================
  // 4. İŞ BİRLİĞİ TAMAMLAMA ORANI (-15 puan max) ⭐⭐
  // Aktif başvuru oranı düşükse iş birlikleri tamamlanamıyor
  // ============================================
  if (record.totalApplications && record.totalApplications > 0) {
    const activeAppRatio = record.activeApplicationsToTotalApplicationsRatio || 0
    const activeApps = record.activeApplications || 0
    
    // Çok düşük aktif oran = süreç sıkışmış
    if (activeAppRatio < 0.01) {
      score -= 15 // KRİTİK: %1'den az aktif
    } else if (activeAppRatio < 0.03) {
      score -= 12 // ÇOK YÜKSEK: %3'ten az
    } else if (activeAppRatio < 0.1) {
      score -= 8 // YÜKSEK: %10'dan az
    } else if (activeAppRatio < 0.2) {
      score -= 4 // DİKKAT: %20'den az
    }
    
    // Başvuru var ama hiç aktif yoksa
    if (activeApps === 0 && record.totalApplications > 10) {
      score -= 10 // Ek ceza: Hiç ilerleyen başvuru yok
    }
  }

  // ============================================
  // 5. MALİYET VERİMLİLİĞİ (-10 puan max) ⭐
  // Çok yüksek CPA = Bütçe verimli eritilmiyor
  // ============================================
  if (record.costPerApplication && record.costPerApplication > 0) {
    if (record.costPerApplication > 10000) {
      score -= 10 // KRİTİK: 10K+ CPA
    } else if (record.costPerApplication > 8000) {
      score -= 8 // ÇOK YÜKSEK: 8K+ CPA
    } else if (record.costPerApplication > 5000) {
      score -= 5 // YÜKSEK: 5K+ CPA
    } else if (record.costPerApplication > 3000) {
      score -= 3 // DİKKAT: 3K+ CPA
    }
  }

  // ============================================
  // BONUS PUANLAR (Pozitif göstergeler) +15 max
  // ============================================
  
  // Yüksek harcama oranı = Bütçe iyi eritiliyor (+5)
  if (record.totalSpentToTotalBalanceRatio && record.totalSpentToTotalBalanceRatio >= 0.6) {
    score += 5
  }
  
  // Düşük bloke oranı = İş birlikleri tamamlanıyor (+5)
  if (record.totalBlockedToTotalBalanceRatio !== undefined && 
      record.totalBlockedToTotalBalanceRatio < 0.1 &&
      record.totalBalance && record.totalBalance > 0) {
    score += 5
  }
  
  // Yüksek aktif kampanya oranı = Süreç hızlı ilerliyor (+5)
  if (record.activeCampaignsToTotalCampaignsRatio && 
      record.activeCampaignsToTotalCampaignsRatio >= 0.5 &&
      record.activeCampaigns && record.activeCampaigns >= 2) {
    score += 5
  }

  // Clamp to 0-100
  return Math.min(100, Math.max(0, score))
}

const CustomerSuccess = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const userEmail = useSelector(selectUser)?.replace(/"/g, "") || ""
  const abortControllerRef = useRef<AbortController | null>(null)

  const [page, setPage] = useState(1)
  const PAGE_SIZES = [10, 20, 30, 50, 100]
  const [totalRecords, setTotalRecords] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZES[1])
  const [initialRecords, setInitialRecords] = useState<BrandListRecord[]>([])
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus>({
    columnAccessor: "created_at",
    direction: "desc",
  })
  const [error, setError] = useState<string | null>(null)
  const [sortByColumn, setSortByColumn] = useState("created_at")
  const [sortOrder, setSortOrder] = useState(true)
  const [loading, setLoading] = useState(false)
  const [quizzes, setQuizzes] = useState<Quiz[]>([])
  const [isFiltersOpen, setIsFiltersOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")

  // Modal states
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false)
  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false)
  const [isOperationsModalOpen, setIsOperationsModalOpen] = useState(false)
  const [isStagesModalOpen, setIsStagesModalOpen] = useState(false)
  const [isEcommerceModalOpen, setIsEcommerceModalOpen] = useState(false)
  const [isBrandSizeModalOpen, setIsBrandSizeModalOpen] = useState(false)
  const [isBrandStateHistoryModalOpen, setIsBrandStateHistoryModalOpen] = useState(false)
  const [isResponsibleAdminModalOpen, setIsResponsibleAdminModalOpen] = useState(false)
  const [isCpmAimModalOpen, setIsCpmAimModalOpen] = useState(false)
  const [isLeadStyleModalOpen, setIsLeadStyleModalOpen] = useState(false)
  const [isUtmModalOpen, setIsUtmModalOpen] = useState(false)
  const [isReferenceModalOpen, setIsReferenceModalOpen] = useState(false)
  const [selectedBrandId, setSelectedBrandId] = useState<string>("")
  const [selectedBrandNotes, setSelectedBrandNotes] = useState<Note[]>([])
  const [selectedBrandMeetings, setSelectedBrandMeetings] = useState<Meeting[]>([])
  const [selectedCampaignNotes, setSelectedCampaignNotes] = useState<CampaignNote[]>([])
  const [selectedBrandStages, setSelectedBrandStages] = useState<Stage[]>([])
  const [selectedEcommerceValue, setSelectedEcommerceValue] = useState<string>("")
  const [selectedBrandSize, setSelectedBrandSize] = useState<string>("")
  const [selectedBrandName, setSelectedBrandName] = useState<string>("")
  const [selectedBrandStateHistory, setSelectedBrandStateHistory] = useState<BrandStateHistoryItem[]>([])
  const [selectedResponsibleAdmins, setSelectedResponsibleAdmins] = useState<string[]>([])
  const [selectedCpmAim, setSelectedCpmAim] = useState<number>(0)
  const [selectedLeadStyle, setSelectedLeadStyle] = useState<
    "dominance" | "influencing" | "cautious" | "stabilizing" | ""
  >("")
  const [selectedUtm, setSelectedUtm] = useState<Brand["utm"]>({})
  const [selectedBrandOperations, setSelectedBrandOperations] =
    useState<BrandOperationsState>(defaultBrandOperationsState)
  const [selectedBrandReference, setSelectedBrandReference] = useState<string>("")
  const [isOpenCategoryModal, setIsOpenCategoryModal] = useState(false)
  const [isOpenClassificationModal, setIsOpenClassificationModal] = useState(false)
  const [selectedClassification, setSelectedClassification] = useState<Classification | null>(null)
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set())
  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false)
  
  // AI Health Analysis Modal
  const [isAIHealthModalOpen, setIsAIHealthModalOpen] = useState(false)
  const [selectedAIData, setSelectedAIData] = useState<any>(null)
  
  // AI Filters State
  const [aiFilters, setAIFilters] = useState<AIFilters>(defaultAIFilters)
  
  // Expanded column groups state (like AllBrands)
  const [expandedGroups, setExpandedGroups] = useState<{
    identity: boolean
    financial: boolean
    notes: boolean
    marketing: boolean
    campaign: boolean
    application: boolean
    quiz: boolean
  }>({
    identity: true,
    financial: false,
    notes: false,
    marketing: false,
    campaign: false,
    application: false,
    quiz: false,
  })

  // Inline edit states for sales readiness and heardFrom
  const [editRateId, setEditRateId] = useState<string | null>(null)
  const [rateValues, setRateValues] = useState<Record<string, number | undefined>>({})
  const [editHeardFromId, setEditHeardFromId] = useState<string | null>(null)
  const [heardFromValues, setHeardFromValues] = useState<Record<string, string>>({})

  // Column group toggle handler
  const handleGroupToggle = (groupKey: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey as keyof typeof prev],
    }))
  }

  // Handle sorting - this triggers API call with new sort params
  // For health_score, health_score_2, and AI columns (calculated/nested fields), only update sortStatus for client-side sorting
  const clientSideSortColumns = [
    "health_score", 
    "health_score_2", 
    "ai_health_score", 
    "ai_status", 
    "ai_priority", 
    "ai_urgency", 
    "ai_churn", 
    "ai_action",
    "ai_days_login",
    "ai_stuck",
    "ai_balance_usage",
    "ai_risks",
    "ai_analyzed"
  ]
  
  const handleSorting = (columnAccessor: string, direction: "asc" | "desc") => {
    if (clientSideSortColumns.includes(columnAccessor)) {
      // Client-side sorting only - don't trigger API call
      setSortStatus({
        columnAccessor,
        direction,
      })
    } else {
      setSortByColumn(columnAccessor)
      setSortOrder(direction === "desc")
      setSortStatus({
        columnAccessor,
        direction,
      })
    }
  }

  const [params, setParams] = useState<BrandListFilters>({
    min_created_at: null,
    max_created_at: null,
    min_last_login: null,
    max_last_login: null,
    brand_name: "",
    email: "",
    first_name: "",
    last_name: "",
    active_campaigns: "true",
    stage: [""],
    brand_state: [""],
    responsible_admin: [],
    process: {
      meeting_planned: "",
      information_mail_sent: "",
      directed_to_agency_based_work: "",
      demo_campaign_created: "",
      balance_loaded: "",
      campaign_management_continues: "",
      campaign_completed: "",
      personal_account: "",
    },
    classification: "",
    reference: "",
  })

  useEffect(() => {
    dispatch(setPageTitle("Customer Success Panel"))
  })

  useEffect(() => {
    handleFetchData()
    getQuizzes()
  }, [sortByColumn, sortOrder, page, pageSize, params])

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  useEffect(() => {
    setPage(1)
  }, [pageSize, params, sortByColumn, sortOrder])

  const handleFetchData = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()

    setLoading(true)
    setError(null)
    try {
      const newParams = {
        min_created_at: params.min_created_at ? params.min_created_at.toISOString() : "",
        max_created_at: params.max_created_at ? params.max_created_at.toISOString() : "",
        min_last_login: params.min_last_login ? params.min_last_login.toISOString() : "",
        max_last_login: params.max_last_login ? params.max_last_login.toISOString() : "",
        brand_name: params.brand_name,
        email: params.email,
        first_name: params.first_name,
        last_name: params.last_name,
        active_campaigns: params.active_campaigns,
        stage: params.stage.join(";"),
        brand_state: params.brand_state.join(";"),
        responsible_admin: params.responsible_admin.join(";"),
        meeting_planned: params.process.meeting_planned.toString(),
        information_mail_sent: params.process.information_mail_sent.toString(),
        directed_to_agency_based_work: params.process.directed_to_agency_based_work.toString(),
        demo_campaign_created: params.process.demo_campaign_created.toString(),
        balance_loaded: params.process.balance_loaded.toString(),
        campaign_management_continues: params.process.campaign_management_continues.toString(),
        campaign_completed: params.process.campaign_completed.toString(),
        personal_account: params.process.personal_account.toString(),
        classification: params.classification,
      }
      const queryParams = new URLSearchParams(newParams as Record<string, string>).toString()
      const response = (await TAfindAllBrands(
        sortByColumn,
        sortOrder,
        page,
        pageSize,
        queryParams,
        abortControllerRef.current.signal
      )) as BrandListApiResponse
      if (response && Array.isArray(response.brands)) {
        const totalLength = response.brands.length
        const totalDocumentsCount = response.totalDocuments
        const data = response.brands.map((item, index) => {
          return {
            ...item,
            id: totalLength - index,
          }
        })

        setInitialRecords(data)
        setTotalRecords(totalDocumentsCount)
        setLoading(false)
      } else {
        setLoading(false)
        setError("No data found")
      }
    } catch (err: unknown) {
      if (isAbortError(err)) {
        setLoading(false)
        return
      }

      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }

      setLoading(false)
      setError("Error fetching data")
    }
  }

  const getQuizzes = async () => {
    try {
      const response = (await TAgetQuizzes("tr")) as Quiz[]
      if (response && Array.isArray(response)) {
        setQuizzes(response)
      }
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
    }
  }

  // All handler functions from AllBrands
  const handleUpdateNote = async (brandId: string, note?: string) => {
    try {
      const updatedBrand = await TAupdateNote(brandId, userEmail, note)
      setInitialRecords((prev) =>
        prev.map((brand) =>
          brand._id === brandId ? { ...brand, notes: updatedBrand.notes || [] } : brand
        )
      )
      return (updatedBrand?.notes ?? []) as Note[]
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      setError(`Error updating note, ${getErrorMessage(err)}`)
      return undefined
    }
  }

  const handleDeleteNote = async (_id: string, index: number) => {
    try {
      await TAdeleteNote(_id, index)
      const updatedBrands = initialRecords.map((brand) => {
        if (brand._id === _id) {
          return { ...brand, notes: brand.notes?.filter((_, i) => i !== index) || [] }
        }
        return brand
      })
      setInitialRecords([...updatedBrands])
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      setError(`Error deleting note, ${getErrorMessage(err)}`)
    }
  }

  const handleECommerce = async (_id: string, ecommerce: string) => {
    try {
      const response = await TAbrandECommerce(_id, ecommerce)
      if (response) {
        setInitialRecords((prevRecords) =>
          prevRecords.map((record) =>
            record._id === _id
              ? {
                  ...record,
                  customer_success_input: {
                    ...(record.customer_success_input || {}),
                    e_commerce_infrastructure: ecommerce,
                  },
                }
              : record
          )
        )
      }
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      setError(`Error updating e-commerce, ${getErrorMessage(err)}`)
    }
  }

  const handleBrandSize = async (_id: string, size: string) => {
    try {
      const response = await TAbrandSize(_id, size)
      if (response) {
        setInitialRecords((prevRecords) =>
          prevRecords.map((record) =>
            record._id === _id ? { ...record, brand_size: size as Brand["brand_size"] } : record
          )
        )
      }
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      setError(`Error updating brand size, ${getErrorMessage(err)}`)
    }
  }

  const handleBrandStage = async (_id: string, stages: Stage[]) => {
    try {
      await TAupdateBrandStage(_id, stages)
      setInitialRecords((prevRecords) =>
        prevRecords.map((record) => (record._id === _id ? { ...record, stages: stages } : record))
      )
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      setError(`Error updating stage, ${getErrorMessage(err)}`)
    }
  }

  const handleMeetingPlanned = async (_id: string, status: boolean) => {
    try {
      await TAmeetingPlanned(_id, status)
      setInitialRecords((prevRecords) =>
        prevRecords.map((record) =>
          record._id === _id ? { ...record, meeting_planned: status } : record
        )
      )
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      setError(`Error updating meeting planned, ${getErrorMessage(err)}`)
    }
  }

  const handleInformationMailSent = async (_id: string, status: boolean) => {
    try {
      await TAinformationMailSent(_id, status)
      setInitialRecords((prevRecords) =>
        prevRecords.map((record) =>
          record._id === _id ? { ...record, information_mail_sent: status } : record
        )
      )
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      setError(`Error updating information mail sent, ${getErrorMessage(err)}`)
    }
  }

  const handleDirectedToAgencyBasedWork = async (_id: string, status: boolean) => {
    try {
      await TAdirectedToAgencyBasedWork(_id, status)
      setInitialRecords((prevRecords) =>
        prevRecords.map((record) =>
          record._id === _id ? { ...record, directed_to_agency_based_work: status } : record
        )
      )
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      setError(`Error updating directed to agency based work, ${getErrorMessage(err)}`)
    }
  }

  const handleDemoCampaignCreated = async (_id: string, status: boolean) => {
    try {
      await TAdemoCampaignCreated(_id, status)
      setInitialRecords((prevRecords) =>
        prevRecords.map((record) =>
          record._id === _id ? { ...record, demo_campaign_created: status } : record
        )
      )
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      setError(`Error updating demo campaign created, ${getErrorMessage(err)}`)
    }
  }

  const handleBalanceLoaded = async (_id: string, status: boolean) => {
    try {
      await TAbalanceLoaded(_id, status)
      setInitialRecords((prevRecords) =>
        prevRecords.map((record) =>
          record._id === _id ? { ...record, balance_loaded: status } : record
        )
      )
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      setError(`Error updating balance loaded, ${getErrorMessage(err)}`)
    }
  }

  const handleCampaignManagementContinues = async (_id: string, status: boolean) => {
    try {
      await TAcampaignManagementContinues(_id, status)
      setInitialRecords((prevRecords) =>
        prevRecords.map((record) =>
          record._id === _id ? { ...record, campaign_management_continues: status } : record
        )
      )
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      setError(`Error updating campaign management continues, ${getErrorMessage(err)}`)
    }
  }

  const handleCampaignCompleted = async (_id: string, status: boolean) => {
    try {
      await TAcampaignCompleted(_id, status)
      setInitialRecords((prevRecords) =>
        prevRecords.map((record) =>
          record._id === _id ? { ...record, campaign_completed: status } : record
        )
      )
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      setError(`Error updating campaign completed, ${getErrorMessage(err)}`)
    }
  }

  const handlePersonalAccount = async (_id: string, status: boolean) => {
    try {
      await TApersonalAccount(_id, status)
      setInitialRecords((prevRecords) =>
        prevRecords.map((record) =>
          record._id === _id ? { ...record, personal_account: status } : record
        )
      )
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      setError(`Error updating personal account, ${getErrorMessage(err)}`)
    }
  }

  const handleSpamBrand = async (_id: string) => {
    try {
      await TASpamBrand(_id, "true")
      setInitialRecords((prevRecords) => prevRecords.filter((record) => record._id !== _id))
      setTotalRecords((prevTotal) => prevTotal - 1)
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      setError(`Error marking brand as spam, ${getErrorMessage(err)}`)
    }
  }

  const handleSalesReadiness = async (_id: string, rate: number) => {
    try {
      await TAbrandSalesReadiness(_id, rate)
      setInitialRecords((prev) =>
        prev.map((brand) =>
          brand._id === _id
            ? {
                ...brand,
                customer_success_input: { ...brand.customer_success_input, sales_readiness: rate },
              }
            : brand
        )
      )
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      setError(`Error updating sales readiness, ${getErrorMessage(err)}`)
    }
  }

  const handleHeardFrom = async (_id: string, heardFrom: string) => {
    try {
      await TAbrandHeardFrom(_id, heardFrom)
      setInitialRecords((prev) =>
        prev.map((brand) => (brand._id === _id ? { ...brand, heardFrom: heardFrom } : brand))
      )
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      setError(`Error updating heard from, ${getErrorMessage(err)}`)
    }
  }

  const handleCpmAim = async (_id: string, cpmAim: number) => {
    try {
      await TAbrandSetCpmAim(_id, cpmAim)
      setInitialRecords((prev) =>
        prev.map((brand) => (brand._id === _id ? { ...brand, cpm_aim: cpmAim } : brand))
      )
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      setError(`Error updating CPM Aim, ${getErrorMessage(err)}`)
    }
  }

  const handleLeadStyle = async (
    _id: string,
    leadStyle: "dominance" | "influencing" | "cautious" | "stabilizing"
  ) => {
    try {
      await TAbrandSetLeadStyle(_id, leadStyle)
      setInitialRecords((prev) =>
        prev.map((brand) => (brand._id === _id ? { ...brand, lead_style: leadStyle } : brand))
      )
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      setError(`Error updating Lead Style, ${getErrorMessage(err)}`)
    }
  }

  const handleUtmUpdate = async (
    _id: string,
    utm: { utm_source?: string; utm_medium?: string; utm_campaign?: string }
  ) => {
    try {
      await TAbrandSetUtm(_id, utm)
      setInitialRecords((prev) =>
        prev.map((brand) => (brand._id === _id ? { ...brand, utm: utm } : brand))
      )
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      setError(`Error updating UTM parameters, ${getErrorMessage(err)}`)
    }
  }

  const onOpenNotesModal = async (brandId: string, notes: Note[]) => {
    setSelectedBrandId(brandId)
    setSelectedBrandNotes(notes)
    try {
      const campaignNotes = (await TAgetCampaignNotes(brandId)) as CampaignNote[]
      setSelectedCampaignNotes(campaignNotes || [])
    } catch {
      setSelectedCampaignNotes([])
    }
    setIsNotesModalOpen(true)
  }

  const onOpenMeetingModal = (brandId: string, meetings: Meeting[] = []) => {
    setSelectedBrandId(brandId)
    setSelectedBrandMeetings(meetings || [])
    setIsMeetingModalOpen(true)
  }

  const handleAddMeeting = async (brandId: string, meeting: MeetingPayload) => {
    try {
      const response = await TAupdateBrandMeeting(brandId, meeting, userEmail)
      if (response) {
        const updatedMeetings = (response.meetings || []) as Meeting[]
        setInitialRecords((prev) =>
          prev.map((brand) =>
            brand._id === brandId ? { ...brand, meetings: updatedMeetings } : brand
          )
        )
        setSelectedBrandMeetings(updatedMeetings)
      }
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      setError(`Error adding meeting, ${getErrorMessage(err)}`)
    }
  }

  const handleDeleteMeeting = async (brandId: string, index: number) => {
    try {
      const response = await TAdeleteBrandMeeting(brandId, index)
      if (response) {
        const updatedMeetings = (response.meetings || []) as Meeting[]
        setInitialRecords((prev) =>
          prev.map((brand) =>
            brand._id === brandId ? { ...brand, meetings: updatedMeetings } : brand
          )
        )
        setSelectedBrandMeetings(updatedMeetings)
      }
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      setError(`Error deleting meeting, ${getErrorMessage(err)}`)
    }
  }

  const handleReferenceChange = async (_id: string, reference: string) => {
    try {
      await TAbrandReferenceChange(_id, reference)
      setInitialRecords((prev) =>
        prev.map((brand) => (brand._id === _id ? { ...brand, reference } : brand))
      )
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      setError(`Error updating reference, ${getErrorMessage(err)}`)
    }
  }

  const onOpenOperationsModal = (brandId: string, operations: BrandOperationsState) => {
    setSelectedBrandId(brandId)
    setSelectedBrandOperations({ ...defaultBrandOperationsState, ...operations })
    setIsOperationsModalOpen(true)
  }

  const onOpenStagesModal = (brandId: string, stages: Stage[]) => {
    setSelectedBrandId(brandId)
    setSelectedBrandStages(stages)
    setIsStagesModalOpen(true)
  }

  const onOpenEcommerceModal = (brandId: string, currentValue: string) => {
    setSelectedBrandId(brandId)
    setSelectedEcommerceValue(currentValue)
    setIsEcommerceModalOpen(true)
  }

  const onOpenBrandSizeModal = (brandId: string, currentSize: string) => {
    setSelectedBrandId(brandId)
    setSelectedBrandSize(currentSize)
    setIsBrandSizeModalOpen(true)
  }

  const onOpenBrandStateHistoryModal = (
    brandId: string,
    brandName: string,
    brandStateHistory: BrandStateHistoryItem[] = []
  ) => {
    setSelectedBrandId(brandId)
    setSelectedBrandName(brandName)
    setSelectedBrandStateHistory(brandStateHistory)
    setIsBrandStateHistoryModalOpen(true)
  }

  const onOpenResponsibleAdminModal = (brandId: string, currentAdmins?: string[]) => {
    setSelectedBrandId(brandId)
    setSelectedResponsibleAdmins(currentAdmins || [])
    setIsResponsibleAdminModalOpen(true)
  }

  const onOpenCpmAimModal = (brandId: string, currentValue?: number) => {
    setSelectedBrandId(brandId)
    setSelectedCpmAim(currentValue || 0)
    setIsCpmAimModalOpen(true)
  }

  const onOpenLeadStyleModal = (
    brandId: string,
    currentValue?: "dominance" | "influencing" | "cautious" | "stabilizing"
  ) => {
    setSelectedBrandId(brandId)
    setSelectedLeadStyle(currentValue || "")
    setIsLeadStyleModalOpen(true)
  }

  const onOpenUtmModal = (brandId: string, currentUtm?: Brand["utm"]) => {
    setSelectedBrandId(brandId)
    setSelectedUtm(currentUtm || {})
    setIsUtmModalOpen(true)
  }

  const onOpenReferenceModal = (brandId: string, reference?: string) => {
    setSelectedBrandId(brandId)
    setSelectedBrandReference(reference || "")
    setIsReferenceModalOpen(true)
  }

  const onOpenClassificationModal = (
    brandId: string,
    currentClassification?: Classification | null
  ) => {
    setSelectedBrandId(brandId)
    if (
      currentClassification &&
      Array.isArray(currentClassification) &&
      currentClassification.length > 0
    ) {
      setSelectedClassification(currentClassification)
    } else {
      setSelectedClassification(null)
    }
    setIsOpenClassificationModal(true)
  }

  const handleClassificationChange = async (brandId: string, classification: Classification) => {
    try {
      if (!classification || classification.length === 0) {
        await TAsetBrandClassification(brandId, "", [])
        setInitialRecords((prev) =>
          prev.map((brand) =>
            brand._id === brandId ? { ...brand, main_category: "", sub_categories: [] } : brand
          )
        )
        return
      }
      const mainCategory = classification[0]?.main_category?.category || ""
      const subCategoriesMap = new Map<string, number>()
      classification.forEach((item) => {
        item.sub_categories?.forEach((sub) => {
          if (sub.category) {
            const existing = subCategoriesMap.get(sub.category)
            if (!existing || sub.confidence > existing) {
              subCategoriesMap.set(sub.category, sub.confidence)
            }
          }
        })
      })
      const subCategories = Array.from(subCategoriesMap.entries()).map(([name, confidence]) => ({
        name,
        confidence,
      }))
      await TAsetBrandClassification(brandId, mainCategory, subCategories)
      setInitialRecords((prev) =>
        prev.map((brand) =>
          brand._id === brandId
            ? { ...brand, main_category: mainCategory, sub_categories: subCategories }
            : brand
        )
      )
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      setError(`Error updating classification, ${getErrorMessage(err)}`)
    }
  }

  const handleAssignResponsibleAdmin = async (brandId: string, adminEmails: string[]) => {
    try {
      await TAsetResponsibleAdmin(brandId, adminEmails)
      setInitialRecords((prevRecords) =>
        prevRecords.map((record) =>
          record._id === brandId ? { ...record, responsible_admin: adminEmails } : record
        )
      )
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      throw err
    }
  }

  const handleOperationChange = async (operation: BrandOperationKey, value: boolean) => {
    setSelectedBrandOperations((prev) => ({ ...prev, [operation]: value }))
    setInitialRecords((prev) =>
      prev.map((brand) =>
        brand._id === selectedBrandId ? { ...brand, [operation]: value } : brand
      )
    )
    try {
      switch (operation) {
        case "meeting_planned":
          await handleMeetingPlanned(selectedBrandId, value)
          break
        case "information_mail_sent":
          await handleInformationMailSent(selectedBrandId, value)
          break
        case "directed_to_agency_based_work":
          await handleDirectedToAgencyBasedWork(selectedBrandId, value)
          break
        case "demo_campaign_created":
          await handleDemoCampaignCreated(selectedBrandId, value)
          break
        case "balance_loaded":
          await handleBalanceLoaded(selectedBrandId, value)
          break
        case "campaign_management_continues":
          await handleCampaignManagementContinues(selectedBrandId, value)
          break
        case "campaign_completed":
          await handleCampaignCompleted(selectedBrandId, value)
          break
        case "personal_account":
          await handlePersonalAccount(selectedBrandId, value)
          break
      }
    } catch (err: unknown) {
      setError(`Error updating ${operation}: ${getErrorMessage(err)}`)
    }
  }

  const toggleSelectAll = () => {
    if (selectedRows.size === initialRecords.length) {
      setSelectedRows(new Set())
    } else {
      setSelectedRows(new Set(initialRecords.map((r) => r._id)))
    }
  }

  const toggleSelectRow = (id: string) => {
    const newSelected = new Set(selectedRows)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedRows(newSelected)
  }

  const filteredRecords = (() => {
    let records = initialRecords.filter((record) => {
      // Text search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase()
        const matchesSearch = (
          record.brand_name?.toLowerCase().includes(search) ||
          record.email?.toLowerCase().includes(search) ||
          record.first_name?.toLowerCase().includes(search) ||
          record.last_name?.toLowerCase().includes(search)
        )
        if (!matchesSearch) {
          return false
        }
      }

      // AI Filters
      const aiData = record.ai_health_analysis

      // AI Status Filter
      if (aiFilters.ai_status.length > 0) {
        const status = aiData?.labels?.status
        if (!status || !aiFilters.ai_status.includes(status)) {
          return false
        }
      }

      // AI Priority Filter
      if (aiFilters.ai_priority.length > 0) {
        const priority = aiData?.labels?.priority_rank
        if (!priority || !aiFilters.ai_priority.includes(priority)) {
          return false
        }
      }

      // AI Health Score Range Filter
      if (aiFilters.ai_min_health_score !== null || aiFilters.ai_max_health_score !== null) {
        const score = aiData?.labels?.health_score
        if (score === undefined) {
          return false
        }
        if (aiFilters.ai_min_health_score !== null && score < aiFilters.ai_min_health_score) {
          return false
        }
        if (aiFilters.ai_max_health_score !== null && score > aiFilters.ai_max_health_score) {
          return false
        }
      }

      // AI Action Type Filter
      if (aiFilters.ai_action_type.length > 0) {
        const actionType = aiData?.labels?.action_type
        if (!actionType || !aiFilters.ai_action_type.includes(actionType)) {
          return false
        }
      }

      // AI Action Required Filter
      if (aiFilters.ai_action_required === "true") {
        const actionRequired = aiData?.labels?.action_required
        if (!actionRequired) {
          return false
        }
      }

      return true
    })

    // Client-side sorting for health_score (calculated field)
    if (sortStatus.columnAccessor === "health_score") {
      records = [...records].sort((a, b) => {
        const scoreA = calculateHealthScore(a)
        const scoreB = calculateHealthScore(b)
        return sortStatus.direction === "asc" ? scoreA - scoreB : scoreB - scoreA
      })
    }

    // Client-side sorting for health_score_2 (calculated field)
    if (sortStatus.columnAccessor === "health_score_2") {
      records = [...records].sort((a, b) => {
        const scoreA = calculateHealthScore2(a)
        const scoreB = calculateHealthScore2(b)
        return sortStatus.direction === "asc" ? scoreA - scoreB : scoreB - scoreA
      })
    }

    // Client-side sorting for AI Health Score
    if (sortStatus.columnAccessor === "ai_health_score") {
      records = [...records].sort((a, b) => {
        const scoreA = a.ai_health_analysis?.labels?.health_score ?? -1
        const scoreB = b.ai_health_analysis?.labels?.health_score ?? -1
        return sortStatus.direction === "asc" ? scoreA - scoreB : scoreB - scoreA
      })
    }

    // Client-side sorting for AI Status
    if (sortStatus.columnAccessor === "ai_status") {
      const statusOrder = { critical: 0, warning: 1, stable: 2, healthy: 3 }
      records = [...records].sort((a, b) => {
        const orderA = statusOrder[a.ai_health_analysis?.labels?.status as keyof typeof statusOrder] ?? 99
        const orderB = statusOrder[b.ai_health_analysis?.labels?.status as keyof typeof statusOrder] ?? 99
        return sortStatus.direction === "asc" ? orderA - orderB : orderB - orderA
      })
    }

    // Client-side sorting for AI Priority
    if (sortStatus.columnAccessor === "ai_priority") {
      records = [...records].sort((a, b) => {
        const prioA = a.ai_health_analysis?.labels?.priority_rank || "P9"
        const prioB = b.ai_health_analysis?.labels?.priority_rank || "P9"
        return sortStatus.direction === "asc" ? prioA.localeCompare(prioB) : prioB.localeCompare(prioA)
      })
    }

    // Client-side sorting for AI Urgency
    if (sortStatus.columnAccessor === "ai_urgency") {
      records = [...records].sort((a, b) => {
        const urgA = a.ai_health_analysis?.labels?.urgency_score ?? -1
        const urgB = b.ai_health_analysis?.labels?.urgency_score ?? -1
        return sortStatus.direction === "asc" ? urgA - urgB : urgB - urgA
      })
    }

    // Client-side sorting for AI Churn
    if (sortStatus.columnAccessor === "ai_churn") {
      records = [...records].sort((a, b) => {
        const churnA = a.ai_health_analysis?.labels?.churn_probability ?? -1
        const churnB = b.ai_health_analysis?.labels?.churn_probability ?? -1
        return sortStatus.direction === "asc" ? churnA - churnB : churnB - churnA
      })
    }

    // Client-side sorting for AI Days Since Login
    if (sortStatus.columnAccessor === "ai_days_login") {
      records = [...records].sort((a, b) => {
        const daysA = a.ai_health_analysis?.quick_stats?.days_since_login ?? 9999
        const daysB = b.ai_health_analysis?.quick_stats?.days_since_login ?? 9999
        return sortStatus.direction === "asc" ? daysA - daysB : daysB - daysA
      })
    }

    // Client-side sorting for AI Stuck Applications
    if (sortStatus.columnAccessor === "ai_stuck") {
      records = [...records].sort((a, b) => {
        const stuckA = a.ai_health_analysis?.quick_stats?.stuck_applications ?? -1
        const stuckB = b.ai_health_analysis?.quick_stats?.stuck_applications ?? -1
        return sortStatus.direction === "asc" ? stuckA - stuckB : stuckB - stuckA
      })
    }

    // Client-side sorting for AI Balance Usage
    if (sortStatus.columnAccessor === "ai_balance_usage") {
      records = [...records].sort((a, b) => {
        const usageA = a.ai_health_analysis?.quick_stats?.balance_usage_percent ?? -1
        const usageB = b.ai_health_analysis?.quick_stats?.balance_usage_percent ?? -1
        return sortStatus.direction === "asc" ? usageA - usageB : usageB - usageA
      })
    }

    // Client-side sorting for AI Risk Count
    if (sortStatus.columnAccessor === "ai_risks") {
      records = [...records].sort((a, b) => {
        const risksA = a.ai_health_analysis?.risk_factors?.length ?? -1
        const risksB = b.ai_health_analysis?.risk_factors?.length ?? -1
        return sortStatus.direction === "asc" ? risksA - risksB : risksB - risksA
      })
    }

    // Client-side sorting for AI Analyzed Date
    if (sortStatus.columnAccessor === "ai_analyzed") {
      records = [...records].sort((a, b) => {
        const dateA = a.ai_health_analysis?.analyzed_at ? new Date(a.ai_health_analysis.analyzed_at).getTime() : 0
        const dateB = b.ai_health_analysis?.analyzed_at ? new Date(b.ai_health_analysis.analyzed_at).getTime() : 0
        return sortStatus.direction === "asc" ? dateA - dateB : dateB - dateA
      })
    }

    return records
  })()

  // Get column configuration (same as AllBrands)
  const columnGroupsConfig = columnConfig({
    handleSpamBrand,
    quizzes,
    handleSalesReadiness,
    handleHeardFrom,
    editRateId,
    setEditRateId,
    rateValues,
    setRateValues,
    editHeardFromId,
    setEditHeardFromId,
    heardFromValues,
    setHeardFromValues,
    onOpenNotesModal,
    onOpenMeetingModal,
    onOpenOperationsModal,
    onOpenStagesModal,
    onOpenEcommerceModal,
    onOpenBrandSizeModal,
    onOpenBrandStateHistoryModal,
    onOpenResponsibleAdminModal,
    onOpenCpmAimModal,
    onOpenLeadStyleModal,
    onOpenUtmModal,
    onOpenReferenceModal,
    onOpenClassificationModal,
  })

  // Build final columns array based on expanded groups
  const getFinalColumns = () => {
    const finalColumns: any[] = []

    // 1. Identity & Core Columns (Always visible or in Identity Group)
    if (expandedGroups.identity) {
      finalColumns.push({
        accessor: "id",
        title: "Id",
        sortable: false,
        render: (record: BrandListRecord) => <span className="text-[11px] font-bold text-[#94A3B8]">#{record.id}</span>
      })

      finalColumns.push({
        accessor: "details",
        title: "Detaylar",
        sortable: false,
        render: (record: BrandListRecord) => (
          <a href={`/brands/find/${record._id}`} target="_blank" rel="noopener noreferrer" className="text-[#2563EB] hover:text-[#1D4ED8]">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
          </a>
        )
      })

      finalColumns.push({
        accessor: "brand_name",
        title: "Marka İsmi",
        sortable: true,
        render: (record: BrandListRecord) => {
          const avatarColor = getAvatarColor(record.brand_name)
          return (
            <div className="flex items-center">
              <div className={`h-10 w-10 rounded-xl ${avatarColor.bg} ${avatarColor.text} flex items-center justify-center text-xs font-bold mr-4 flex-shrink-0 shadow-sm border border-white`}>
                {getInitials(record.brand_name)}
              </div>
              <span className="text-sm font-bold text-[#1E293B] truncate hover:text-[#2563EB] cursor-pointer" onClick={() => navigate(`/brands/find/${record._id}`)}>
                {record.brand_name || "Unknown"}
              </span>
            </div>
          )
        }
      })

      finalColumns.push({
        accessor: "brand_state",
        title: "Status",
        sortable: true,
        render: (record: BrandListRecord) => <button onClick={() => onOpenBrandStateHistoryModal(record._id, record.brand_name, record.brandStateHistory)}><StatusChip brandState={record.brand_state || ""} /></button>
      })

      finalColumns.push({
        accessor: "brand_state_details",
        title: "Durum Detayları",
        sortable: false,
        render: (record: BrandListRecord) => (
          <button onClick={() => onOpenBrandStateHistoryModal(record._id, record.brand_name, record.brandStateHistory)} className="text-[#64748B] hover:text-[#2563EB]">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </button>
        )
      })

      finalColumns.push({
        accessor: "brand_size",
        title: "Boyut",
        sortable: true,
        render: (record: BrandListRecord) => (
          <span className="px-2 py-1 bg-[#F1F5F9] text-[#475569] rounded-md text-[11px] font-bold uppercase cursor-pointer" onClick={() => onOpenBrandSizeModal(record._id, record.brand_size || "small")}>
            {record.brand_size === "large" ? "Büyük" : record.brand_size === "medium" ? "Orta" : "Küçük"}
          </span>
        )
      })

      finalColumns.push({ accessor: "sector", title: "Sektör", sortable: true, render: (record: BrandListRecord) => <span className="text-xs font-semibold">{record.sector === "agency" ? "Ajans" : "Marka"}</span> })
      finalColumns.push({ accessor: "first_name", title: "İsim", sortable: true })
      finalColumns.push({ accessor: "last_name", title: "Soyisim", sortable: true })
      finalColumns.push({ accessor: "email", title: "Email", sortable: true, render: (record: BrandListRecord) => <span className="text-xs text-[#2563EB] hover:underline">{record.email}</span> })
      finalColumns.push({ accessor: "phone", title: "Telefon", sortable: true, render: (record: BrandListRecord) => <span className="text-xs text-[#64748B] whitespace-nowrap">{record.phone || "-"}</span> })
      finalColumns.push({ accessor: "classification", title: "Sınıflandırma", sortable: true, render: (record: BrandListRecord) => <button className="text-xs text-[#64748B] hover:text-[#2563EB] truncate max-w-[120px]" onClick={() => onOpenClassificationModal(record._id, null)}>{record.main_category || "Seçilmemiş"}</button> })
      finalColumns.push({ accessor: "created_at", title: "Oluşturulma", sortable: true, render: (record: BrandListRecord) => <span className="text-xs text-[#64748B]">{formatDate(record.created_at)}</span> })
      finalColumns.push({ accessor: "last_login", title: "Son Giriş", sortable: true, render: (record: BrandListRecord) => <span className="text-xs font-bold text-[#1E293B]">{formatDate(record.last_login)}</span> })
    }

    // NOTE: Health Score and Ops Score columns removed - using AI Score instead

    // ============================================
    // AI HEALTH ANALYSIS COLUMNS (GPT o1 Results)
    // ============================================

    // AI Health Score (from GPT o1)
    finalColumns.push({
      accessor: "ai_health_score",
      title: "AI Score",
      sortable: true,
      render: (record: BrandListRecord) => {
        const aiData = record.ai_health_analysis
        const score = aiData?.labels?.health_score
        if (score === undefined) {
          return <span className="text-[10px] text-[#94A3B8]">-</span>
        }
        const colorClass = score >= 70 ? "bg-[#10B981]" : score >= 50 ? "bg-[#3B82F6]" : score >= 30 ? "bg-[#F59E0B]" : "bg-[#EF4444]"
        const bgClass = score >= 70 ? "bg-[#ECFDF5]" : score >= 50 ? "bg-[#EFF6FF]" : score >= 30 ? "bg-[#FFFBEB]" : "bg-[#FEF2F2]"
        return (
          <div className="flex flex-col w-20">
            <div className={`w-full ${bgClass} rounded-full h-2 mb-1 overflow-hidden`}>
              <div className={`${colorClass} h-full rounded-full transition-all duration-500`} style={{ width: `${score}%` }}></div>
            </div>
            <span className="text-[11px] font-bold text-[#1E293B]">{score}</span>
          </div>
        )
      }
    })

    // AI Status Badge
    finalColumns.push({
      accessor: "ai_status",
      title: "AI Durum",
      sortable: true,
      render: (record: BrandListRecord) => {
        const status = record.ai_health_analysis?.labels?.status
        if (!status) {
          return <span className="text-[10px] text-[#94A3B8]">-</span>
        }
        
        const statusConfig: Record<string, { bg: string; text: string; label: string; icon: string }> = {
          critical: { bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-700 dark:text-red-300", label: "Kritik", icon: "🔴" },
          warning: { bg: "bg-amber-100 dark:bg-amber-900/30", text: "text-amber-700 dark:text-amber-300", label: "Dikkat", icon: "🟠" },
          stable: { bg: "bg-blue-100 dark:bg-blue-900/30", text: "text-blue-700 dark:text-blue-300", label: "Stabil", icon: "🔵" },
          healthy: { bg: "bg-emerald-100 dark:bg-emerald-900/30", text: "text-emerald-700 dark:text-emerald-300", label: "Sağlıklı", icon: "🟢" },
        }
        const config = statusConfig[status] || statusConfig.stable
        return (
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold ${config.bg} ${config.text}`}>
            <span>{config.icon}</span>
            {config.label}
          </span>
        )
      }
    })

    // AI Priority Rank
    finalColumns.push({
      accessor: "ai_priority",
      title: "Öncelik",
      sortable: true,
      render: (record: BrandListRecord) => {
        const priority = record.ai_health_analysis?.labels?.priority_rank
        if (!priority) {
          return <span className="text-[10px] text-[#94A3B8]">-</span>
        }
        
        const priorityConfig: Record<string, { color: string }> = {
          P0: { color: "bg-red-600 text-white" },
          P1: { color: "bg-orange-500 text-white" },
          P2: { color: "bg-amber-500 text-white" },
          P3: { color: "bg-blue-500 text-white" },
          P4: { color: "bg-slate-400 text-white" },
          P5: { color: "bg-slate-300 text-slate-700" },
        }
        const config = priorityConfig[priority] || priorityConfig.P3
        return (
          <span className={`px-2 py-1 rounded-md text-[10px] font-bold ${config.color}`}>
            {priority}
          </span>
        )
      }
    })

    // AI Urgency Score
    finalColumns.push({
      accessor: "ai_urgency",
      title: "Aciliyet",
      sortable: true,
      render: (record: BrandListRecord) => {
        const urgency = record.ai_health_analysis?.labels?.urgency_score
        if (urgency === undefined) {
          return <span className="text-[10px] text-[#94A3B8]">-</span>
        }
        const color = urgency >= 70 ? "text-red-600" : urgency >= 40 ? "text-amber-600" : "text-blue-600"
        return <span className={`text-sm font-bold ${color}`}>{urgency}</span>
      }
    })

    // AI Churn Probability
    finalColumns.push({
      accessor: "ai_churn",
      title: "Churn %",
      sortable: true,
      render: (record: BrandListRecord) => {
        const churn = record.ai_health_analysis?.labels?.churn_probability
        if (churn === undefined) {
          return <span className="text-[10px] text-[#94A3B8]">-</span>
        }
        const color = churn >= 60 ? "text-red-600 bg-red-50" : churn >= 30 ? "text-amber-600 bg-amber-50" : "text-emerald-600 bg-emerald-50"
        return (
          <span className={`px-2 py-1 rounded-md text-[10px] font-bold ${color}`}>
            %{churn}
          </span>
        )
      }
    })

    // AI Action Type
    finalColumns.push({
      accessor: "ai_action",
      title: "Aksiyon",
      sortable: true,
      render: (record: BrandListRecord) => {
        const actionType = record.ai_health_analysis?.labels?.action_type
        if (!actionType || actionType === "none") {
          return <span className="text-[10px] text-[#94A3B8]">-</span>
        }
        
        const actionLabels: Record<string, { label: string; icon: string; color: string }> = {
          outreach: { label: "İletişim", icon: "📞", color: "bg-blue-50 text-blue-700" },
          meeting: { label: "Toplantı", icon: "📅", color: "bg-purple-50 text-purple-700" },
          follow_up: { label: "Takip", icon: "↩️", color: "bg-indigo-50 text-indigo-700" },
          escalate: { label: "Eskalasyon", icon: "⬆️", color: "bg-red-50 text-red-700" },
          support: { label: "Destek", icon: "🛠️", color: "bg-amber-50 text-amber-700" },
          training: { label: "Eğitim", icon: "📚", color: "bg-teal-50 text-teal-700" },
          upsell: { label: "Upsell", icon: "💰", color: "bg-emerald-50 text-emerald-700" },
          monitor: { label: "İzle", icon: "👁️", color: "bg-slate-50 text-slate-700" },
        }
        const config = actionLabels[actionType] || { label: actionType, icon: "📋", color: "bg-slate-50 text-slate-700" }
        return (
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium ${config.color}`}>
            <span>{config.icon}</span>
            {config.label}
          </span>
        )
      }
    })

    // AI Quick Stats - Days Since Login
    finalColumns.push({
      accessor: "ai_days_login",
      title: "Son Giriş",
      sortable: true,
      render: (record: BrandListRecord) => {
        const days = record.ai_health_analysis?.quick_stats?.days_since_login
        if (days === undefined) {
          return <span className="text-[10px] text-[#94A3B8]">-</span>
        }
        const color = days <= 7 ? "text-emerald-600" : days <= 30 ? "text-blue-600" : days <= 90 ? "text-amber-600" : "text-red-600"
        return <span className={`text-xs font-bold ${color}`}>{days}g</span>
      }
    })

    // AI Quick Stats - Stuck Applications
    finalColumns.push({
      accessor: "ai_stuck",
      title: "Takılı",
      sortable: true,
      render: (record: BrandListRecord) => {
        const stuck = record.ai_health_analysis?.quick_stats?.stuck_applications
        if (stuck === undefined) {
          return <span className="text-[10px] text-[#94A3B8]">-</span>
        }
        if (stuck === 0) {
          return <span className="text-xs text-emerald-600 font-bold">0</span>
        }
        const color = stuck >= 10 ? "text-red-600 bg-red-50" : stuck >= 5 ? "text-amber-600 bg-amber-50" : "text-blue-600 bg-blue-50"
        return <span className={`px-2 py-0.5 rounded text-xs font-bold ${color}`}>{stuck}</span>
      }
    })

    // AI Quick Stats - Balance Usage %
    finalColumns.push({
      accessor: "ai_balance_usage",
      title: "Bütçe Kull.",
      sortable: true,
      render: (record: BrandListRecord) => {
        const usage = record.ai_health_analysis?.quick_stats?.balance_usage_percent
        if (usage === undefined) {
          return <span className="text-[10px] text-[#94A3B8]">-</span>
        }
        const color = usage >= 60 ? "text-emerald-600" : usage >= 30 ? "text-blue-600" : usage >= 10 ? "text-amber-600" : "text-red-600"
        return <span className={`text-xs font-bold ${color}`}>%{usage.toFixed(0)}</span>
      }
    })

    // AI Summary (Truncated with Tooltip)
    finalColumns.push({
      accessor: "ai_summary",
      title: "AI Özet",
      sortable: false,
      render: (record: BrandListRecord) => {
        const summary = record.ai_health_analysis?.ai_summary
        if (!summary) {
          return <span className="text-[10px] text-[#94A3B8]">-</span>
        }
        const truncated = summary.length > 50 ? summary.substring(0, 50) + "..." : summary
        return (
          <div className="relative group">
            <span className="text-[10px] text-[#64748B] max-w-[150px] block truncate cursor-help">
              {truncated}
            </span>
            {/* Tooltip on hover */}
            <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-50 w-72 max-w-xs">
              <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg">
                <div className="flex items-start gap-2">
                  <span className="text-yellow-400 mt-0.5">💡</span>
                  <span className="leading-relaxed">{summary}</span>
                </div>
                <div className="absolute -bottom-1 left-4 w-2 h-2 bg-gray-900 transform rotate-45"></div>
              </div>
            </div>
          </div>
        )
      }
    })

    // AI Risk Count
    finalColumns.push({
      accessor: "ai_risks",
      title: "Risk",
      sortable: true,
      render: (record: BrandListRecord) => {
        const risks = record.ai_health_analysis?.risk_factors
        if (!risks || risks.length === 0) {
          return <span className="text-[10px] text-emerald-600 font-bold">0</span>
        }
        const criticalCount = risks.filter(r => r.severity === "critical").length
        const highCount = risks.filter(r => r.severity === "high").length
        const color = criticalCount > 0 ? "bg-red-100 text-red-700" : highCount > 0 ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"
        return (
          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${color}`}>
            {risks.length}
          </span>
        )
      }
    })

    // AI Analyzed Date
    finalColumns.push({
      accessor: "ai_analyzed",
      title: "Analiz",
      sortable: true,
      render: (record: BrandListRecord) => {
        const analyzed = record.ai_health_analysis?.analyzed_at
        if (!analyzed) {
          return <span className="text-[10px] text-[#94A3B8]">-</span>
        }
        const date = new Date(analyzed)
        const now = new Date()
        const hoursDiff = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))
        const isFresh = hoursDiff < 24
        return (
          <span className={`text-[10px] ${isFresh ? "text-emerald-600 font-bold" : "text-[#64748B]"}`}>
            {date.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit" })}
          </span>
        )
      }
    })

    // AI Detail Button
    finalColumns.push({
      accessor: "ai_detail",
      title: "🤖",
      sortable: false,
      render: (record: BrandListRecord) => (
        <button
          onClick={() => {
            setSelectedBrandId(record._id)
            setSelectedBrandName(record.brand_name || "")
            setSelectedAIData(record.ai_health_analysis || null)
            setIsAIHealthModalOpen(true)
          }}
          className={`flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md ${
            record.ai_health_analysis?.labels?.status === "critical" 
              ? "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700" 
              : record.ai_health_analysis?.labels?.status === "warning"
                ? "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                : record.ai_health_analysis?.labels?.status === "healthy"
                  ? "bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600"
                  : "bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600"
          } text-white`}
          title="AI Sağlık Analizi Detayları"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>
      )
    })

    // 2. Financial Columns
    if (expandedGroups.financial) {
      finalColumns.push({ accessor: "balance", title: "Bakiye", sortable: true, render: (record: BrandListRecord) => <span className="font-bold text-[#1E293B]">{formatCurrency(record.balance)}</span> })
      finalColumns.push({ accessor: "totalBlocks", title: "Bloke", sortable: true, render: (record: BrandListRecord) => <span className="text-[#64748B]">{formatCurrency(record.totalBlocks)}</span> })
      finalColumns.push({ accessor: "totalBalance", title: "Toplam Yüklenen", sortable: true, render: (record: BrandListRecord) => <span className="font-bold text-[#1E293B]">{formatCurrency(record.totalBalance)}</span> })
      finalColumns.push({ accessor: "totalSpentBalance", title: "Toplam Harcanan", sortable: true, render: (record: BrandListRecord) => <span className="text-[#64748B]">{formatCurrency(record.totalSpentBalance)}</span> })
      finalColumns.push({ accessor: "balanceTotalBudgetRatio", title: "Bakiye/Bütçe %", sortable: true, render: (record: BrandListRecord) => <span className="text-xs font-bold">{((record.balanceTotalBudgetRatio || 0) * 100).toFixed(1)}%</span> })
      finalColumns.push({ accessor: "totalSpentToTotalBalanceRatio", title: "Harcanan/Bakiye %", sortable: true, render: (record: BrandListRecord) => <span className="text-xs font-bold text-[#059669]">{((record.totalSpentToTotalBalanceRatio || 0) * 100).toFixed(1)}%</span> })
      finalColumns.push({ accessor: "totalBlockedToTotalBalanceRatio", title: "Bloke/Bakiye %", sortable: true, render: (record: BrandListRecord) => <span className="text-xs font-bold text-[#DC2626]">{((record.totalBlockedToTotalBalanceRatio || 0) * 100).toFixed(1)}%</span> })
    }

    // 3. Notes & Contact
    if (expandedGroups.notes) {
      finalColumns.push({ 
        accessor: "is_spam", 
        title: "Spam", 
        sortable: true, 
        render: (record: BrandListRecord) => (
          <button onClick={() => handleSpamBrand(record._id)} className="text-[#10B981] hover:text-[#DC2626]">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
          </button>
        ) 
      })
      finalColumns.push({ accessor: "notes", title: "Notlar", sortable: false, render: (record: BrandListRecord) => <button onClick={() => onOpenNotesModal(record._id, record.notes || [])} className="text-xs font-bold text-[#2563EB] bg-[#EFF6FF] px-2 py-1 rounded-lg">{record.notes?.length || 0} Not</button> })
      finalColumns.push({ accessor: "meetings", title: "Toplantılar", sortable: false, render: (record: BrandListRecord) => <button onClick={() => onOpenMeetingModal(record._id, record.meetings || [])} className="text-xs font-bold text-[#D97706] bg-[#FFFBEB] px-2 py-1 rounded-lg">{record.meetings?.length || 0} Toplantı</button> })
      finalColumns.push({ 
        accessor: "responsible_admin", 
        title: "Admin", 
        sortable: true,
        render: (record: BrandListRecord) => {
          const admins = getResponsibleAdminsArray(record.responsible_admin)
          return (
            <div className="flex -space-x-2 overflow-hidden cursor-pointer" onClick={() => onOpenResponsibleAdminModal(record._id, admins)}>
              {admins && admins.length > 0 ? (
                admins.slice(0, 3).map((email, i) => (
                  <div key={i} className="inline-block h-7 w-7 rounded-full ring-2 ring-white bg-[#F1F5F9] flex items-center justify-center text-[10px] font-bold text-[#475569]">
                    {email.charAt(0).toUpperCase()}
                  </div>
                ))
              ) : <span className="text-[10px] text-[#94A3B8] font-bold">+ Ekle</span>}
            </div>
          )
        }
      })
      finalColumns.push({ accessor: "sales_readiness", title: "Sıcaklık", sortable: true, render: (record: BrandListRecord) => <span className="text-sm font-bold text-[#1E293B]">{record.customer_success_input?.sales_readiness || "-"}</span> })
    }

    // 4. Marketing & Strategy
    if (expandedGroups.marketing) {
      finalColumns.push({ accessor: "cpm_aim", title: "CPM Aim", sortable: true, render: (record: BrandListRecord) => <button onClick={() => onOpenCpmAimModal(record._id, record.cpm_aim)} className="text-xs hover:text-[#2563EB]">{record.cpm_aim || "-"}</button> })
      finalColumns.push({ accessor: "lead_style", title: "Lead Style", sortable: true, render: (record: BrandListRecord) => <button onClick={() => onOpenLeadStyleModal(record._id, record.lead_style)} className="text-[10px] font-bold uppercase text-[#64748B] bg-[#F8FAFC] border border-[#E2E8F0] px-2 py-1 rounded-md">{record.lead_style || "-"}</button> })
      finalColumns.push({ accessor: "e_commerce", title: "E-Ticaret", sortable: true, render: (record: BrandListRecord) => <button onClick={() => onOpenEcommerceModal(record._id, record.customer_success_input?.e_commerce_infrastructure || "")} className="text-xs">{record.customer_success_input?.e_commerce_infrastructure || "-"}</button> })
      finalColumns.push({ accessor: "utm", title: "UTM", sortable: false, render: (record: BrandListRecord) => <button onClick={() => onOpenUtmModal(record._id, record.utm)} className="text-[10px] text-[#64748B] max-w-[100px] truncate">{record.utm?.utm_source || "-"}</button> })
      finalColumns.push({ accessor: "heardFrom", title: "Duyuldu", sortable: true, render: (record: BrandListRecord) => <span className="text-xs">{record.heardFrom || "-"}</span> })
      finalColumns.push({ accessor: "stages", title: "Aşamalar", sortable: false, render: (record: BrandListRecord) => <button onClick={() => onOpenStagesModal(record._id, record.stages || [])} className="text-[10px] font-bold text-[#2563EB] uppercase">{record.stages?.[0]?.stage || "Seç"}</button> })
      finalColumns.push({ 
        accessor: "brand_operations", 
        title: "İşlemler", 
        sortable: false,
        render: (record: BrandListRecord) => {
          const ops: string[] = []
          if (record.meeting_planned) {
            ops.push("Toplantı")
          }
          if (record.balance_loaded) {
            ops.push("Bakiye")
          }
          return <button onClick={() => onOpenOperationsModal(record._id, defaultBrandOperationsState)} className="text-[10px] font-medium text-[#64748B]">{ops.length > 0 ? ops.join(", ") : "İşlem Yok"}</button>
        }
      })
    }

    // 5. Campaign Data
    if (expandedGroups.campaign) {
      finalColumns.push({ accessor: "totalCampaigns", title: "T. Kampanya", sortable: true, render: (record: BrandListRecord) => <span className="font-bold">{record.totalCampaigns || 0}</span> })
      finalColumns.push({ accessor: "activeCampaigns", title: "Aktif", sortable: true, render: (record: BrandListRecord) => <span className="text-[#059669] font-bold">{record.activeCampaigns || 0}</span> })
      finalColumns.push({ accessor: "inactiveCampaigns", title: "Pasif", sortable: true })
      finalColumns.push({ accessor: "draftCampaigns", title: "Taslak", sortable: true })
      finalColumns.push({ accessor: "activeCampaignsToTotalCampaignsRatio", title: "Aktif %", sortable: true, render: (record: BrandListRecord) => <span className="text-xs font-bold text-[#64748B]">{((record.activeCampaignsToTotalCampaignsRatio || 0) * 100).toFixed(1)}%</span> })
      finalColumns.push({ accessor: "inactiveCampaignsToTotalCampaignsRatio", title: "Pasif %", sortable: true, render: (record: BrandListRecord) => <span className="text-xs font-bold text-[#64748B]">{((record.inactiveCampaignsToTotalCampaignsRatio || 0) * 100).toFixed(1)}%</span> })
      finalColumns.push({ accessor: "draftCampaignsToTotalCampaignsRatio", title: "Taslak %", sortable: true, render: (record: BrandListRecord) => <span className="text-xs font-bold text-[#64748B]">{((record.draftCampaignsToTotalCampaignsRatio || 0) * 100).toFixed(1)}%</span> })
    }

    // 6. Application Data
    if (expandedGroups.application) {
      finalColumns.push({ accessor: "totalApplications", title: "T. Başvuru", sortable: true, render: (record: BrandListRecord) => <span className="font-bold">{record.totalApplications || 0}</span> })
      finalColumns.push({ accessor: "activeApplications", title: "Aktif B.", sortable: true })
      finalColumns.push({ accessor: "inactiveApplications", title: "Pasif B.", sortable: true })
      finalColumns.push({ accessor: "activeApplicationsToTotalApplicationsRatio", title: "Aktif %", sortable: true, render: (record: BrandListRecord) => <span className="text-xs font-bold">{((record.activeApplicationsToTotalApplicationsRatio || 0) * 100).toFixed(1)}%</span> })
      finalColumns.push({ accessor: "inactiveApplicationsToTotalApplicationsRatio", title: "Pasif %", sortable: true, render: (record: BrandListRecord) => <span className="text-xs font-bold">{((record.inactiveApplicationsToTotalApplicationsRatio || 0) * 100).toFixed(1)}%</span> })
      finalColumns.push({ accessor: "costPerApplication", title: "Başvuru Mal.", sortable: true, render: (record: BrandListRecord) => <span className="text-xs font-bold">{formatCurrency(record.costPerApplication)}</span> })
      finalColumns.push({ accessor: "costPerView", title: "Görüntüleme Mal.", sortable: true, render: (record: BrandListRecord) => <span className="text-xs text-[#64748B]">{formatCurrency(record.costPerView)}</span> })
      finalColumns.push({ accessor: "costPerClick", title: "Tıklama Mal.", sortable: true, render: (record: BrandListRecord) => <span className="text-xs text-[#64748B]">{formatCurrency(record.costPerClick)}</span> })
    }

    // 7. Quiz Data
    if (expandedGroups.quiz) {
      finalColumns.push({ 
        accessor: "quiz_exp", 
        title: "Influencer Deneyimi", 
        sortable: false,
        render: (record: BrandListRecord) => {
          const ans = record.quiz_answers?.find(q => q.key?.question === "influencer_marketing_experience")
          return <span className="text-[11px] text-[#64748B]">{ans?.options?.[0]?.optionText || "-"}</span>
        } 
      })
      finalColumns.push({ 
        accessor: "quiz_budget", 
        title: "Yıllık Bütçe", 
        sortable: false,
        render: (record: BrandListRecord) => {
          const ans = record.quiz_answers?.find(q => q.key?.question === "annual_budget")
          return <span className="text-[11px] font-bold text-[#1E293B]">{ans?.options?.[0]?.optionText || "-"}</span>
        } 
      })
    }

    return finalColumns
  }

  // Prepare groups for the GroupedColumns component
  const groups = [
    {
      title: "Kimlik & Temel Bilgiler",
      columns: columnGroupsConfig.identityColumns,
      isExpanded: expandedGroups.identity,
    },
    {
      title: "Bütçe / Finansal Durum",
      columns: columnGroupsConfig.financialColumns,
      isExpanded: expandedGroups.financial,
    },
    {
      title: "Ek Notlar ve İletişim Geçmişi",
      columns: columnGroupsConfig.notesColumns,
      isExpanded: expandedGroups.notes,
    },
    {
      title: "Pazarlama & Strateji Bilgileri",
      columns: columnGroupsConfig.marketingColumns,
      isExpanded: expandedGroups.marketing,
    },
    {
      title: "Kampanya Verileri",
      columns: columnGroupsConfig.campaignColumns,
      isExpanded: expandedGroups.campaign,
    },
    {
      title: "Başvuru Verileri",
      columns: columnGroupsConfig.applicationColumns,
      isExpanded: expandedGroups.application,
    },
    {
      title: "Quiz Soruları",
      columns: columnGroupsConfig.quizColumns,
      isExpanded: expandedGroups.quiz,
    },
  ]

  const columns = getFinalColumns()
  const totalPages = Math.ceil(totalRecords / pageSize)

  return (
    <div className="p-6 bg-[#F8FAFC] min-h-screen">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1E293B] mb-1">Customer Database</h1>
          <p className="text-[#64748B] text-sm font-medium">
            Manage your client relationships and track performance metrics.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => setIsFiltersOpen(true)}
            className="flex items-center px-4 py-2.5 bg-white border border-[#E2E8F0] rounded-xl text-sm font-semibold text-[#475569] hover:bg-[#F1F5F9] transition-all shadow-sm"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filter
          </button>
          
          <div className="relative">
            <button
              onClick={() => setIsColumnSelectorOpen(!isColumnSelectorOpen)}
              className="flex items-center px-4 py-2.5 bg-white border border-[#E2E8F0] rounded-xl text-sm font-semibold text-[#475569] hover:bg-[#F1F5F9] transition-all shadow-sm"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
              </svg>
              Columns
            </button>
            {isColumnSelectorOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-white border border-[#E2E8F0] rounded-2xl shadow-xl z-50 p-4 max-h-[500px] overflow-y-auto">
                <div className="mb-4">
                  <h3 className="font-bold text-[#1E293B] text-sm mb-1">Column Groups</h3>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {groups.map((group, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleGroupToggle([
                          "identity", "financial", "notes", "marketing", "campaign", "application", "quiz"
                        ][idx])}
                        className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                          expandedGroups[["identity", "financial", "notes", "marketing", "campaign", "application", "quiz"][idx] as keyof typeof expandedGroups]
                            ? "bg-[#EFF6FF] text-[#2563EB]"
                            : "bg-[#F1F5F9] text-[#64748B]"
                        }`}
                      >
                        {group.title.split(" ")[0]}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <button className="flex items-center px-4 py-2.5 bg-white border border-[#E2E8F0] rounded-xl text-sm font-semibold text-[#475569] hover:bg-[#F1F5F9] transition-all shadow-sm">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export
          </button>
          
          <button className="flex items-center px-5 py-2.5 bg-[#2563EB] text-white rounded-xl text-sm font-bold hover:bg-[#1D4ED8] transition-all shadow-lg shadow-blue-200">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Add Customer
          </button>
        </div>
      </div>

      {/* AI Health Summary Stats */}
      {(() => {
        const aiRecords = initialRecords.filter(r => r.ai_health_analysis?.labels?.status)
        const criticalCount = aiRecords.filter(r => r.ai_health_analysis?.labels?.status === "critical").length
        const warningCount = aiRecords.filter(r => r.ai_health_analysis?.labels?.status === "warning").length
        const stableCount = aiRecords.filter(r => r.ai_health_analysis?.labels?.status === "stable").length
        const healthyCount = aiRecords.filter(r => r.ai_health_analysis?.labels?.status === "healthy").length
        const avgHealthScore = aiRecords.length > 0 
          ? Math.round(aiRecords.reduce((sum, r) => sum + (r.ai_health_analysis?.labels?.health_score || 0), 0) / aiRecords.length)
          : 0
        const avgChurn = aiRecords.length > 0 
          ? Math.round(aiRecords.reduce((sum, r) => sum + (r.ai_health_analysis?.labels?.churn_probability || 0), 0) / aiRecords.length)
          : 0
        const p0Count = aiRecords.filter(r => r.ai_health_analysis?.labels?.priority_rank === "P0").length
        const p1Count = aiRecords.filter(r => r.ai_health_analysis?.labels?.priority_rank === "P1").length

        if (aiRecords.length === 0) {
          return null
        }

        return (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
            {/* Total Analyzed */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">🤖</span>
                <span className="text-[10px] font-bold text-[#64748B] uppercase">AI Analiz</span>
              </div>
              <p className="text-2xl font-bold text-[#1E293B]">{aiRecords.length}</p>
            </div>
            
            {/* Avg Health Score */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">💯</span>
                <span className="text-[10px] font-bold text-[#64748B] uppercase">Ort. Skor</span>
              </div>
              <p className={`text-2xl font-bold ${avgHealthScore >= 60 ? "text-emerald-600" : avgHealthScore >= 40 ? "text-amber-600" : "text-red-600"}`}>
                {avgHealthScore}
              </p>
            </div>

            {/* Critical */}
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">🔴</span>
                <span className="text-[10px] font-bold text-red-600 uppercase">Kritik</span>
              </div>
              <p className="text-2xl font-bold text-red-700">{criticalCount}</p>
            </div>

            {/* Warning */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">🟠</span>
                <span className="text-[10px] font-bold text-amber-600 uppercase">Dikkat</span>
              </div>
              <p className="text-2xl font-bold text-amber-700">{warningCount}</p>
            </div>

            {/* Stable */}
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">🔵</span>
                <span className="text-[10px] font-bold text-blue-600 uppercase">Stabil</span>
              </div>
              <p className="text-2xl font-bold text-blue-700">{stableCount}</p>
            </div>

            {/* Healthy */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">🟢</span>
                <span className="text-[10px] font-bold text-emerald-600 uppercase">Sağlıklı</span>
              </div>
              <p className="text-2xl font-bold text-emerald-700">{healthyCount}</p>
            </div>

            {/* High Priority (P0+P1) */}
            <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">⚡</span>
                <span className="text-[10px] font-bold text-orange-600 uppercase">Acil (P0+P1)</span>
              </div>
              <p className="text-2xl font-bold text-orange-700">{p0Count + p1Count}</p>
            </div>

            {/* Avg Churn */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">📉</span>
                <span className="text-[10px] font-bold text-[#64748B] uppercase">Ort. Churn</span>
              </div>
              <p className={`text-2xl font-bold ${avgChurn >= 50 ? "text-red-600" : avgChurn >= 30 ? "text-amber-600" : "text-emerald-600"}`}>
                %{avgChurn}
              </p>
            </div>
          </div>
        )
      })()}

      {/* Quick Filters */}
      <div className="flex items-center gap-3 mb-8">
        <span className="text-[11px] font-extrabold text-[#94A3B8] uppercase tracking-[0.1em]">Quick Filters:</span>
        <div className="flex flex-wrap items-center gap-2">
          {params.brand_state.some(s => ["has_balance_loaded", "loaded_balance_multiple_times", "campaign_created_received_applications_collaboration_completed"].includes(s)) && (
            <div className="flex items-center px-3 py-1.5 bg-[#EFF6FF] text-[#2563EB] border border-[#DBEAFE] rounded-full text-xs font-bold shadow-sm">
              Status: Active
              <button 
                className="ml-2 hover:text-[#1D4ED8]"
                onClick={() => {
                  const newStates = params.brand_state.filter(s => !["has_balance_loaded", "loaded_balance_multiple_times", "campaign_created_received_applications_collaboration_completed"].includes(s))
                  setParams({ ...params, brand_state: newStates.length > 0 ? newStates : [""] })
                }}
              >×</button>
            </div>
          )}
          
          <button 
            onClick={() => {
              const activeStates = ["has_balance_loaded", "loaded_balance_multiple_times", "campaign_created_received_applications_collaboration_completed"]
              const isCurrentlyActive = params.brand_state.some(s => activeStates.includes(s))
              if (isCurrentlyActive) {
                const newStates = params.brand_state.filter(s => !activeStates.includes(s))
                setParams({ ...params, brand_state: newStates.length > 0 ? newStates : [""] })
              } else {
                const filteredExisting = params.brand_state.filter(s => s !== "")
                setParams({ ...params, brand_state: [...filteredExisting, ...activeStates] })
              }
            }}
            className={`flex items-center px-3 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer border ${
              params.brand_state.some(s => ["has_balance_loaded", "loaded_balance_multiple_times", "campaign_created_received_applications_collaboration_completed"].includes(s))
                ? "bg-[#EFF6FF] text-[#2563EB] border-[#DBEAFE]"
                : "bg-white text-[#64748B] border-[#E2E8F0] hover:bg-[#F8FAFC]"
            }`}
          >
            Status: Active
          </button>

          {/* Active Campaigns Filter */}
          {params.active_campaigns === "true" && (
            <div className="flex items-center px-3 py-1.5 bg-[#ECFDF5] text-[#059669] border border-[#A7F3D0] rounded-full text-xs font-bold shadow-sm">
              🎯 Aktif Kampanya
              <button 
                className="ml-2 hover:text-[#047857]"
                onClick={() => setParams({ ...params, active_campaigns: "" })}
              >×</button>
            </div>
          )}
          
          <button 
            onClick={() => {
              const isActive = params.active_campaigns === "true"
              setParams({ ...params, active_campaigns: isActive ? "" : "true" })
            }}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer border ${
              params.active_campaigns === "true"
                ? "bg-[#ECFDF5] text-[#059669] border-[#A7F3D0]"
                : "bg-white text-[#64748B] border-[#E2E8F0] hover:bg-[#ECFDF5] hover:border-[#A7F3D0]"
            }`}
          >
            🎯 Aktif Kampanya
          </button>

          {params.brand_state.some(s => ["campaign_created_balance_not_loaded", "campaign_created_has_no_applications"].includes(s)) && (
            <div className="flex items-center px-3 py-1.5 bg-[#FEF2F2] text-[#DC2626] border border-[#FEE2E2] rounded-full text-xs font-bold shadow-sm">
              Health: At Risk
              <button 
                className="ml-2 hover:text-[#991B1B]"
                onClick={() => {
                  const riskStates = ["campaign_created_balance_not_loaded", "campaign_created_has_no_applications"]
                  const newStates = params.brand_state.filter(s => !riskStates.includes(s))
                  setParams({ ...params, brand_state: newStates.length > 0 ? newStates : [""] })
                }}
              >×</button>
            </div>
          )}

          <button 
            onClick={() => {
              const riskStates = ["campaign_created_balance_not_loaded", "campaign_created_has_no_applications"]
              const isCurrentlyAtRisk = params.brand_state.some(s => riskStates.includes(s))
              if (isCurrentlyAtRisk) {
                const newStates = params.brand_state.filter(s => !riskStates.includes(s))
                setParams({ ...params, brand_state: newStates.length > 0 ? newStates : [""] })
              } else {
                const filteredExisting = params.brand_state.filter(s => s !== "")
                setParams({ ...params, brand_state: [...filteredExisting, ...riskStates] })
              }
            }}
            className={`flex items-center px-3 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer border ${
              params.brand_state.some(s => ["campaign_created_balance_not_loaded", "campaign_created_has_no_applications"].includes(s))
                ? "bg-[#FEF2F2] text-[#DC2626] border-[#FEE2E2]"
                : "bg-white text-[#64748B] border-[#E2E8F0] hover:bg-[#F8FAFC]"
            }`}
          >
            Health: At Risk
          </button>

          {(params.brand_state.some(s => s !== "") || params.active_campaigns === "true") && (
            <button 
              className="text-[#2563EB] text-xs font-bold ml-2 hover:underline"
              onClick={() => setParams({ ...params, brand_state: [""], active_campaigns: "" })}
            >Clear All</button>
          )}

          {/* AI Quick Filters Separator */}
          <span className="text-[#E2E8F0] mx-2">|</span>
          
          {/* AI: Critical */}
          <button 
            onClick={() => {
              const isCritical = aiFilters.ai_status.includes("critical")
              if (isCritical) {
                setAIFilters({ ...aiFilters, ai_status: aiFilters.ai_status.filter(s => s !== "critical") })
              } else {
                setAIFilters({ ...aiFilters, ai_status: [...aiFilters.ai_status, "critical"] })
              }
            }}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer border ${
              aiFilters.ai_status.includes("critical")
                ? "bg-red-100 text-red-700 border-red-200"
                : "bg-white text-[#64748B] border-[#E2E8F0] hover:bg-red-50 hover:border-red-200"
            }`}
          >
            🔴 AI Kritik
          </button>

          {/* AI: Warning */}
          <button 
            onClick={() => {
              const isWarning = aiFilters.ai_status.includes("warning")
              if (isWarning) {
                setAIFilters({ ...aiFilters, ai_status: aiFilters.ai_status.filter(s => s !== "warning") })
              } else {
                setAIFilters({ ...aiFilters, ai_status: [...aiFilters.ai_status, "warning"] })
              }
            }}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer border ${
              aiFilters.ai_status.includes("warning")
                ? "bg-amber-100 text-amber-700 border-amber-200"
                : "bg-white text-[#64748B] border-[#E2E8F0] hover:bg-amber-50 hover:border-amber-200"
            }`}
          >
            🟠 AI Dikkat
          </button>

          {/* AI: Action Required (P0-P2) */}
          <button 
            onClick={() => {
              const isActionRequired = aiFilters.ai_action_required === "true"
              setAIFilters({ ...aiFilters, ai_action_required: isActionRequired ? "" : "true" })
            }}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer border ${
              aiFilters.ai_action_required === "true"
                ? "bg-purple-100 text-purple-700 border-purple-200"
                : "bg-white text-[#64748B] border-[#E2E8F0] hover:bg-purple-50 hover:border-purple-200"
            }`}
          >
            ⚡ Aksiyon Gerekli
          </button>

          {/* AI: Low Score (0-30) */}
          <button 
            onClick={() => {
              const isLowScore = aiFilters.ai_max_health_score === 30
              if (isLowScore) {
                setAIFilters({ ...aiFilters, ai_min_health_score: null, ai_max_health_score: null })
              } else {
                setAIFilters({ ...aiFilters, ai_min_health_score: null, ai_max_health_score: 30 })
              }
            }}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer border ${
              aiFilters.ai_max_health_score === 30
                ? "bg-red-100 text-red-700 border-red-200"
                : "bg-white text-[#64748B] border-[#E2E8F0] hover:bg-red-50 hover:border-red-200"
            }`}
          >
            📉 Score 0-30
          </button>

          {/* Clear AI Filters */}
          {(aiFilters.ai_status.length > 0 || aiFilters.ai_priority.length > 0 || aiFilters.ai_action_required === "true" || aiFilters.ai_min_health_score !== null || aiFilters.ai_max_health_score !== null) && (
            <button 
              className="text-purple-600 text-xs font-bold ml-2 hover:underline"
              onClick={() => setAIFilters(defaultAIFilters)}
            >Clear AI</button>
          )}
        </div>
      </div>

      {/* Main Table Card */}
      <div className="bg-white border border-[#E2E8F0] rounded-[24px] shadow-sm overflow-hidden overflow-x-auto">
        <StickyDataTable
          records={filteredRecords}
          columns={columns}
          totalRecords={totalRecords}
          recordsPerPage={pageSize}
          page={page}
          onPageChange={(p) => setPage(p)}
          recordsPerPageOptions={PAGE_SIZES}
          onRecordsPerPageChange={setPageSize}
          sortStatus={sortStatus}
          onSortStatusChange={(e) => handleSorting(e.columnAccessor, e.direction)}
          minHeight={200}
          paginationText={({ from, to, totalRecords }) =>
            `Showing  ${from}-${to} of ${totalRecords}`
          }
          highlightOnHover={true}
          className="modern-database-table"
        />
      </div>

      {/* Styles for the modern look */}
      <style>{`
        .modern-database-table {
          --primary-blue: #2563EB;
          --table-border: #F1F5F9;
          --header-bg: #F8FAFC;
          --text-main: #1E293B;
          --text-muted: #64748B;
        }
        .modern-database-table table {
          border-spacing: 0;
          width: 100%;
        }
        .modern-database-table thead th {
          background: var(--header-bg);
          padding: 16px 20px;
          color: var(--text-muted);
          font-size: 11px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid var(--table-border);
        }
        .modern-database-table tbody td {
          padding: 16px 20px;
          border-bottom: 1px solid var(--table-border);
          color: var(--text-main);
          font-size: 14px;
          font-weight: 500;
        }
        .modern-database-table tbody tr:last-child td {
          border-bottom: none;
        }
        .modern-database-table .pagination-container {
          padding: 20px;
          background: white;
          border-top: 1px solid var(--table-border);
        }
      `}</style>
      {/* Modals */}
      <CategoryModal
        title="Markaları Filtrele"
        isOpen={isOpenCategoryModal}
        onClose={() => setIsOpenCategoryModal(false)}
        onSelect={(classification) => setParams({ ...params, classification })}
        selectedValue={{ category: params.classification }}
      />

      <CustomerSuccessFilters
        isOpen={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        handleFetchData={handleFetchData}
        params={params}
        setParams={setParams}
        aiFilters={aiFilters}
        setAIFilters={setAIFilters}
      />

      <NotesModal
        isOpen={isNotesModalOpen}
        onClose={() => setIsNotesModalOpen(false)}
        brandId={selectedBrandId}
        notes={selectedBrandNotes}
        campaignNotes={selectedCampaignNotes}
        onAddNote={handleUpdateNote}
        onDeleteNote={handleDeleteNote}
      />

      <MeetingModal
        isOpen={isMeetingModalOpen}
        onClose={() => setIsMeetingModalOpen(false)}
        brandId={selectedBrandId}
        meetings={selectedBrandMeetings}
        onAddMeeting={handleAddMeeting}
        onDeleteMeeting={handleDeleteMeeting}
      />

      <BrandOperationsModal
        isOpen={isOperationsModalOpen}
        onClose={() => setIsOperationsModalOpen(false)}
        brandId={selectedBrandId}
        operations={selectedBrandOperations}
        onOperationChange={handleOperationChange}
      />

      <StagesModal
        isOpen={isStagesModalOpen}
        onClose={() => setIsStagesModalOpen(false)}
        brandId={selectedBrandId}
        stages={selectedBrandStages}
        onStageChange={handleBrandStage}
      />

      <EcommerceModal
        isOpen={isEcommerceModalOpen}
        onClose={() => setIsEcommerceModalOpen(false)}
        brandId={selectedBrandId}
        currentValue={selectedEcommerceValue}
        onSave={handleECommerce}
      />

      <BrandSizeModal
        isOpen={isBrandSizeModalOpen}
        onClose={() => setIsBrandSizeModalOpen(false)}
        brandId={selectedBrandId}
        currentValue={selectedBrandSize}
        onSave={handleBrandSize}
      />

      <BrandStateHistoryModal
        isOpen={isBrandStateHistoryModalOpen}
        onClose={() => setIsBrandStateHistoryModalOpen(false)}
        brandId={selectedBrandId}
        brandName={selectedBrandName}
        brandStateHistory={selectedBrandStateHistory}
      />

      <ResponsibleAdminModal
        isOpen={isResponsibleAdminModalOpen}
        onClose={() => setIsResponsibleAdminModalOpen(false)}
        brandId={selectedBrandId}
        currentAdmins={selectedResponsibleAdmins}
        onAssign={handleAssignResponsibleAdmin}
      />

      <CpmAimModal
        isOpen={isCpmAimModalOpen}
        onClose={() => setIsCpmAimModalOpen(false)}
        brandId={selectedBrandId}
        currentValue={selectedCpmAim}
        onSave={handleCpmAim}
      />

      <LeadStyleModal
        isOpen={isLeadStyleModalOpen}
        onClose={() => setIsLeadStyleModalOpen(false)}
        brandId={selectedBrandId}
        currentValue={selectedLeadStyle || undefined}
        onSave={handleLeadStyle}
      />

      <UtmModal
        isOpen={isUtmModalOpen}
        onClose={() => setIsUtmModalOpen(false)}
        brandId={selectedBrandId}
        currentUtm={selectedUtm}
        onUpdateUtm={handleUtmUpdate}
      />

      <ReferenceChangeModal
        isOpen={isReferenceModalOpen}
        onClose={() => setIsReferenceModalOpen(false)}
        brandId={selectedBrandId}
        brandReference={selectedBrandReference}
        onReferenceChange={handleReferenceChange}
      />

      <ClassificationModal
        title="Marka Sınıflandırma"
        isOpen={isOpenClassificationModal}
        onClose={() => setIsOpenClassificationModal(false)}
        onSelect={() => handleClassificationChange(selectedBrandId, selectedClassification || [])}
        selectedClassification={selectedClassification}
        setSelectedClassification={setSelectedClassification}
      />

      {/* AI Health Analysis Modal */}
      <AIHealthAnalysisModal
        isOpen={isAIHealthModalOpen}
        onClose={() => setIsAIHealthModalOpen(false)}
        brandId={selectedBrandId}
        brandName={selectedBrandName}
        initialData={selectedAIData}
      />
    </div>
  )
}

export default CustomerSuccess
